import { useLocalSearchParams, useRouter } from "expo-router";

import { useUpdateWorkRequirementScreeningMutation } from "@/data/mutations/workRequirementsMutations";
import { useGetWorkRequirementQuery } from "@/data/queries/workRequirementsQueries";
import {
  getNextWorkRequirementStepLink,
  WorkRequirementScreeningMemberType,
  WorkRequirementType,
} from "@/domain/workRequirements";

type SaveAndContinueParamsType = {
  lastStep: NonNullable<WorkRequirementType["screeningLastStep"]>;
  members: WorkRequirementScreeningMemberType[];
  // For the hours step: the member whose hours were just reported.
  householdMemberId?: string;
};

// Shared by every screening step: loads the work requirement from the route,
// saves the step's answers and moves on to whichever step comes next.
export const useWorkRequirementScreeningStep = () => {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();

  const { data: workRequirement, isPending: isLoading } =
    useGetWorkRequirementQuery(id);
  const { mutate: updateScreening, isPending: isSaving } =
    useUpdateWorkRequirementScreeningMutation();

  const saveAndContinue = ({
    lastStep,
    members,
    householdMemberId,
  }: SaveAndContinueParamsType) =>
    updateScreening(
      {
        workRequirementId: id,
        screening: { lastStep, isComplete: false, members },
      },
      {
        onSuccess: () => {
          router.navigate(
            getNextWorkRequirementStepLink({
              workRequirementId: id,
              step: lastStep,
              members,
              householdMemberId,
            }),
          );
        },
      },
    );

  const goBack = () => {
    if (router.canGoBack()) {
      router.back();
    } else {
      router.dismissTo("/(authed)");
    }
  };

  return {
    workRequirementId: id,
    workRequirement,
    isLoading,
    isSaving,
    saveAndContinue,
    goBack,
  };
};
